import React, {useMemo} from 'react';
import {useTranslation} from 'next-i18next';
import Placeholder from '@components/Placeholder';
import BarChart from '@components/Charts/BarChart';
import type {
	BarChartSeries,
	BarChartSeriesDataPoint,
	DrilldownDataPoint,
} from '@components/Charts/types';
import type {ContextData} from '@deezer/next/services/api/Legacy/requests/analytic/types';
import AnalyticCard from '@deezer/next/features/Analytic/components/AnalyticCard';
import {notNull} from '@deezer/next/shared/utils/array';
import {Box} from '@tempo/core';

// TODO add typing for type
type ContextsChartProps = {
	type?: string;
	contextsProvider?: ContextData;
	contextsDeezer?: ContextData;
	isLoading: boolean;
	isError: boolean;
};

const ContextsChart = ({
	type,
	contextsProvider,
	contextsDeezer,
	isLoading,
	isError,
}: ContextsChartProps) => {
	const {t} = useTranslation('d4c');

	const title = useMemo(() => {
		switch (type) {
			case 'track':
				return t('analyticsartist_title_howarepeoplelisteningtotrack_d4c-web');
			case 'album':
				return t('analyticsartist_title_howarepeoplelisteningtoalbum_d4c-web');
			case 'artist':
				return t('analyticsartist_title_howarepeoplelisteningtoartist_d4c-web');
			case 'playlist':
				return t('analyticshome_title_howdopeoplelistentoyourcatalog_d4c-web');
			default:
				return t('analyticshome_title_howdopeoplelistentoyourcatalog_d4c-web');
		}
	}, [t, type]);

	const {series, drilldown} = useMemo(() => {
		// TODO add typing
		const translateDataLabel = (label: string) => {
			switch (label) {
				case 'IN PLAYLISTS':
					return t('analyticshome_text_fromplaylists_d4c-web');
				case 'ALBUM PAGES':
					return t('analyticshome_text_albumpages_d4c-web');
				case 'ARTIST PAGES':
					return t('analyticshome_text_artistpages_d4c-web');
				case 'USERS FAVORITE TRACKS':
					return t('analyticshome_text_usersfavoritetracks_d4c-web');
				case 'FLOW OR A MIX':
					return t('analyticshome_text_floworamix_d4c-web');
				case 'PARTNER APPS':
					return t('analyticshome_text_partnerapps_d4c-web');
				case 'THE ALBUM PAGE':
					return t('analyticsartist_text_thealbumpage_d4c-web');
				case 'USER-MADE PLAYLISTS':
					return t('analyticsartist_text_usermadeplaylists_d4c-web');
				case 'EDITOR-MADE PLAYLISTS':
					return t('analyticsartist_text_editormadeplaylists_d4c-web');
				case 'PROVIDER PLAYLISTS':
					return t('analyticsartist_text_providerplaylists_d4c-web');
				case 'THIRD PARTY PLAYLISTS':
					return t('analyticsartist_text_thirdpartyplaylists_d4c-web');
				default:
					return label;
			}
		};

		let seriesLabel = t('analyticshome_text_yourcatalog_d4c-web');
		switch (type) {
			case 'track':
				seriesLabel = t('analyticsartist_text_thistrack_d4c-web');
				break;
			case 'album':
				seriesLabel = t('analyticsartist_text_thisalbum_d4c-web');
				break;
			case 'artist':
				seriesLabel = t('analyticsartist_title_yourartist_d4c-web');
				break;
			case 'playlist':
				seriesLabel = t('analyticsartist_text_thisplaylist_d4c-web');
				break;
			default:
				seriesLabel = t('analyticshome_text_yourcatalog_d4c-web');
				break;
		}

		return [
			contextsProvider
				? {
						name: seriesLabel,
						data: contextsProvider,
					}
				: null,
			contextsDeezer
				? {
						name: t('analyticshome_text_average_d4c-web'),
						data: contextsDeezer,
					}
				: null,
		]
			.filter(notNull)
			.reduce<{
				drilldown: DrilldownDataPoint[];
				drilldownMap: {[key: string]: string};
				series: BarChartSeries[];
			}>(
				(acc, d: {name: string; data: ContextData}) => {
					const serieName = d.name;
					const drilldownData: DrilldownDataPoint[] = [];
					const serie: BarChartSeriesDataPoint[] = [];

					Object.entries(d.data).forEach(([key, value]) => {
						let groupId = undefined;

						if (value.subcontexts) {
							groupId =
								acc.drilldownMap[key] ??
								(Math.random() + 1).toString(36).substring(2);
							const drilldown = {
								groupId,
								name: serieName,
								data: value.subcontexts.map<[string, number]>(
									([key, value]) => [translateDataLabel(key), value],
								),
							};
							acc.drilldownMap[key] = groupId;
							drilldownData.push(drilldown);
						}

						const data = {
							name: translateDataLabel(value.label),
							value: value.percent,
							percent: value.percent,
							groupId,
						};
						serie.push(data);
					});

					acc.drilldown = [...acc.drilldown, ...drilldownData];
					acc.series = [
						...acc.series,
						{
							name: serieName,
							data: serie,
						},
					];
					return acc;
				},
				{drilldown: [], drilldownMap: {}, series: []},
			);
	}, [contextsDeezer, contextsProvider, t, type]);

	return (
		<AnalyticCard
			hasNoData={!contextsDeezer || !contextsProvider}
			title={title}
			data-testid="contexts-chart-card"
			placeholder={<Placeholder />}
			isLoading={isLoading}
			isError={isError}
			variant="overview"
		>
			<Box p="spacing.l">
				<BarChart
					series={series}
					drilldown={drilldown}
					orientation={{base: 'vertical', md: 'horizontal'}}
				/>
			</Box>
		</AnalyticCard>
	);
};

export default ContextsChart;
